"use client";

import { faHeartHalfStroke } from "@fortawesome/pro-duotone-svg-icons";
import { faHeart as faHeartRegular } from "@fortawesome/pro-regular-svg-icons";
import { faHeart } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "@tosize/ui";
import { type CSSProperties, type ComponentProps, useOptimistic } from "react";
import {
	addProductToWishlist,
	removeProductFromWishlist,
} from "~/app/[lang]/(default)/(auth)/customer/wishlist/actions";

interface Props
	extends Pick<WishlistButtonProps, "size" | "variant" | "isOnWishlist" | "messages"> {
	lang: string;
	productId: string;
}

export function WishlistActionButtonClient(props: Props) {
	const { lang, productId: id, isOnWishlist, ...restProps } = props;

	const [isOptimisticOnWishlist, setOptimisticIsOnWishlist] = useOptimistic<boolean | null>(
		isOnWishlist,
	);

	return (
		<WishlistButton
			onClick={async () => {
				if (isOptimisticOnWishlist !== null) {
					setOptimisticIsOnWishlist(!isOptimisticOnWishlist);

					return isOptimisticOnWishlist
						? await removeProductFromWishlist({ lang, id })
						: await addProductToWishlist({ lang, id });
				}
			}}
			isOnWishlist={isOptimisticOnWishlist}
			{...restProps}
		/>
	);
}

export interface WishlistButtonProps extends ComponentProps<"button"> {
	messages: { addToWishlist: string; removeFromWishlist: string };
	size: "small" | "medium";
	variant?: "card" | "pdp" | "default";
	isOnWishlist: boolean | null;
}

export function WishlistButton(props: WishlistButtonProps) {
	const { isOnWishlist, size, variant = "default", className, ...restProps } = props;

	return (
		<button
			title={isOnWishlist ? props.messages.removeFromWishlist : props.messages.addToWishlist}
			type="button"
			className={cn(
				"group flex shrink-0 appearance-none items-center justify-center rounded-full bg-white leading-none shadow outline-none motion-safe:transition-all",
				{
					"h-7 w-7": size === "small",
					"h-9 w-9 text-xl": size === "medium",
					"absolute top-2 right-2": variant === "card",
					"absolute top-4 right-4": variant === "pdp",
					"text-primary-medium first:child:enter:opacity-0": isOnWishlist,
					"text-primary-dark first:child:enter:opacity-100": !isOnWishlist,
					"enter:bg-shades-light": isOnWishlist === null,
				},
				className,
			)}
			{...restProps}
		>
			{isOnWishlist === null ? (
				<FontAwesomeIcon
					icon={faHeartHalfStroke}
					style={
						{
							"--fa-primary-color": "rgb(var(--color-primary-medium))",
							"--fa-secondary-color": "rgb(var(--color-primary-dark))",
							"--fa-secondary-opacity": "1",
						} as CSSProperties
					}
				/>
			) : (
				<>
					<FontAwesomeIcon
						icon={faHeart}
						className={cn("absolute motion-safe:transition-all", { "opacity-0": !isOnWishlist })}
					/>
					<FontAwesomeIcon
						icon={faHeartRegular}
						className={cn({ "text-primary-dark": !isOnWishlist })}
					/>
				</>
			)}
		</button>
	);
}
